export const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

export const isLiveDocumentLink = (vlt) =>
  vlt?.type === 'document' && isHttpUrl(vlt?.url_or_path);

export const isUploadedVltDocument = (vlt) =>
  vlt?.type === 'document' && !isHttpUrl(vlt?.url_or_path);

export const isUploadedVltVideo = (vlt) =>
  vlt?.type === 'video' && !isHttpUrl(vlt?.url_or_path);

export const toDatetimeLocalValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const getMeetingDateStatus = (meetingDatetime) => {
  if (!meetingDatetime) return 'disabled';
  const meeting = new Date(meetingDatetime);
  if (Number.isNaN(meeting.getTime())) return 'disabled';
  const today = new Date();
  const meetingDay = new Date(meeting.getFullYear(), meeting.getMonth(), meeting.getDate());
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (meetingDay < todayDay) return 'past';
  if (meetingDay > todayDay) return 'future';
  return 'today';
};

export const formatMeetingDatetime = (value) => {
  if (!value) return 'Not scheduled';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Not scheduled';
  return d.toLocaleString();
};

export const getInstructorDisplayName = (vlt) => {
  if (vlt?.instructor) {
    const name = `${vlt.instructor.first_name || ''} ${vlt.instructor.last_name || ''}`.trim();
    return name || vlt.instructor.email || 'Instructor';
  }
  return 'TBA';
};

export const parseGroupIds = (value) => {
  let raw = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => String(item));
};

export const getInitialVltFormData = () => ({
  title: '',
  description: '',
  type: 'video',
  url_or_path: '',
  document_source: 'link',
  document_file: null,
  video_source: 'link',
  video_file: null,
  meeting_datetime: '',
  instructor_id: '',
  organization_id: '',
  group_ids: [],
  status: 'active',
  thumbnail_image: null
});

export const mapVltToFormData = (vlt) => ({
  title: vlt.title || '',
  description: vlt.description || '',
  type: vlt.type || 'video',
  url_or_path: (vlt.type === 'document' && !isHttpUrl(vlt.url_or_path))
    || (vlt.type === 'video' && !isHttpUrl(vlt.url_or_path))
    ? ''
    : (vlt.url_or_path || ''),
  document_source: vlt.type === 'document'
    ? (isHttpUrl(vlt.url_or_path) ? 'link' : 'upload')
    : 'link',
  document_file: null,
  video_source: vlt.type === 'video'
    ? (isHttpUrl(vlt.url_or_path) ? 'link' : 'upload')
    : 'link',
  video_file: null,
  meeting_datetime: vlt.type === 'link' ? toDatetimeLocalValue(vlt.meeting_datetime) : '',
  instructor_id: vlt.instructor_id ? String(vlt.instructor_id) : '',
  organization_id: vlt.organization_id ? String(vlt.organization_id) : '',
  group_ids: parseGroupIds(vlt.group_ids),
  status: vlt.status || 'active',
  thumbnail_image: null,
  existing_document_path: vlt.type === 'document' && !isHttpUrl(vlt.url_or_path) ? vlt.url_or_path : '',
  existing_video_path: vlt.type === 'video' && !isHttpUrl(vlt.url_or_path) ? vlt.url_or_path : ''
});

export const validateVltForm = (formData, { editingVlt = null } = {}) => {
  const errors = {};

  if (!formData.title?.trim()) {
    errors.title = 'Title is required';
  }

  if (formData.type === 'document') {
    if (formData.document_source === 'link') {
      if (!formData.url_or_path?.trim()) {
        errors.url_or_path = 'Document link is required';
      } else if (!isHttpUrl(formData.url_or_path)) {
        errors.url_or_path = 'Enter a valid document URL (http or https)';
      }
    } else if (!formData.document_file && !formData.existing_document_path && !(
      editingVlt?.type === 'document' && !isHttpUrl(editingVlt?.url_or_path)
    )) {
      errors.document_file = 'Please upload a PDF or Word document';
    }
  } else if (formData.type === 'video') {
    if (formData.video_source === 'link') {
      if (!formData.url_or_path?.trim()) {
        errors.url_or_path = 'Video link is required';
      } else if (!isHttpUrl(formData.url_or_path)) {
        errors.url_or_path = 'Enter a valid video URL (http or https)';
      }
    } else if (!formData.video_file && !formData.existing_video_path && !(
      editingVlt?.type === 'video' && !isHttpUrl(editingVlt?.url_or_path)
    )) {
      errors.video_file = 'Please upload a video file (MP4, WebM, or MOV)';
    }
  } else if (formData.type === 'link') {
    if (!formData.url_or_path?.trim()) {
      errors.url_or_path = 'Meeting link is required';
    } else if (!isHttpUrl(formData.url_or_path)) {
      errors.url_or_path = 'Enter a valid meeting URL (http or https)';
    }
    if (!formData.meeting_datetime) {
      errors.meeting_datetime = 'Meeting date and time is required';
    }
    if (!formData.instructor_id) {
      errors.instructor_id = 'Instructor is required';
    }
  }

  return errors;
};

export const appendVltFormData = (formData, { includeOrganization = true } = {}) => {
  const fd = new FormData();

  fd.append('title', formData.title);
  fd.append('description', formData.description || '');
  fd.append('type', formData.type);
  fd.append('status', formData.status || 'active');

  if (formData.type === 'document') {
    fd.append('document_source', formData.document_source);
    if (formData.document_source === 'link') {
      fd.append('url_or_path', formData.url_or_path);
    }
  } else if (formData.type === 'video') {
    fd.append('video_source', formData.video_source);
    if (formData.video_source === 'link') {
      fd.append('url_or_path', formData.url_or_path);
    }
  } else if (formData.type === 'link') {
    fd.append('url_or_path', formData.url_or_path);
    fd.append('meeting_datetime', formData.meeting_datetime);
    fd.append('instructor_id', formData.instructor_id);
  }

  if (includeOrganization) {
    fd.append('organization_id', formData.organization_id || '');
  }

  const numericGroupIds = (formData.group_ids || [])
    .map((id) => parseInt(id, 10))
    .filter((id) => !Number.isNaN(id));
  fd.append('group_ids', JSON.stringify(numericGroupIds));

  if (formData.thumbnail_image) {
    fd.append('thumbnail_image', formData.thumbnail_image);
  }

  if (formData.type === 'document' && formData.document_source === 'upload' && formData.document_file) {
    fd.append('document_file', formData.document_file);
  }

  if (formData.type === 'video' && formData.video_source === 'upload' && formData.video_file) {
    fd.append('video_file', formData.video_file);
  }

  return fd;
};
